import { useLocation, useNavigate } from 'react-router';
import GreenCheckIcon from '../../assets/icons/check-green-icon.svg?react';
import BlueLinkIcon from '../../assets/icons/blue-link-icon.svg?react';
import clsx from 'clsx';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  AuthApiAuthControllerVerifyEmailRequest,
  ResendVerificationDTO,
} from '@EHSNavigator/backend';
import { useEffect, useState, useRef } from 'react';
import { useApiClients } from '../../client/api/useApiClient';
import Loader from '../../components/Loader';

export default function Verify() {
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [verifiedMessage, setVerifiedMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const userId = query.get('userId');
  const token = query.get('token');
  const verificationAttempted = useRef(false);

  const { AuthApi } = useApiClients();

  const authVerifyMutation = useMutation<
    any,
    Error,
    AuthApiAuthControllerVerifyEmailRequest
  >({
    mutationFn: async (data: AuthApiAuthControllerVerifyEmailRequest) => {
      const response = await AuthApi.authControllerVerifyEmail({
        ...data,
      });
      return response.data;
    },

    onSuccess: (data) => {
      toast.success(data?.message);
      setIsLoading(false);
    },
    onError: (error: any) => {
      setIsLoading(false);
      const errorMessage = error?.response?.data?.message;
      if (errorMessage.includes('expired')) {
        setIsExpired(true);
      }
      if (!Array.isArray(errorMessage)) {
        setVerifiedMessage(errorMessage);
      }
    },
  });

  useEffect(() => {
    if (userId && token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      authVerifyMutation.mutate({
        token: String(token),
        userId: String(userId),
      });
    }
  }, [userId, token, authVerifyMutation]);

  const mutation = useMutation<any, Error, ResendVerificationDTO>({
    mutationFn: async (data: ResendVerificationDTO) => {
      const response = await AuthApi.authControllerResendVerification({
        resendVerificationDTO: {
          ...data,
        },
      });
      navigate(
        '/sign-in?message=Activation%20link%20sent—check%20your%20email%20to%20finish%20signing%20up.',
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log(data?.message);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });
  const buttonSize = useButtonSizeLarge();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <main className="relative z-10 flex flex-1 flex-col items-center justify-between gap-10 pb-5">
      <div className="flex flex-1 flex-col justify-center">
        {isExpired ? (
          <div className="mx-auto flex max-w-685 flex-col items-center gap-12 px-25">
            <BlueLinkIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
            <div className="flex flex-col gap-2.5 text-center">
              <h2 className={clsx('text-neutral-tones-text font-bold')}>
                Link Expired
              </h2>
              <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
                you can request a new link by clicking Resend verification email
                button below
              </p>
            </div>
            <Button
              disable={mutation?.isPending}
              onClick={() => mutation.mutate({ userId: String(userId) })}
              otherClasses={clsx('max-w-465 w-full')}
              size={buttonSize}
            >
              Resend Verification Email
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-12 px-25">
            <GreenCheckIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
            <div className="flex flex-col gap-2.5 text-center">
              <h2 className={clsx('text-neutral-tones-text font-bold')}>
                {verifiedMessage != ''
                  ? verifiedMessage
                  : 'Your Account Has Been Verified'}
              </h2>
              <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
                {verifiedMessage != '' && verifiedMessage.includes('not found')
                  ? `Click the button below to render back to the sign in page`
                  : verifiedMessage.includes('Server Error')
                    ? `Oops! Something went wrong. Click the button below to render back to the sign in page`
                    : `You can now sign in and continue using EHS Navigator!`}
              </p>
            </div>
            <Button
              onClick={() => navigate('/sign-in')}
              otherClasses={clsx('max-w-465 w-full')}
              size={buttonSize}
            >
              Sign In
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
